import type { ChatHistoryResponse, ChatMessageResponse, TranscriptEntry } from "./types";

const DEFAULT_MESSAGE_TIMEOUT_MS = 60000;
const SCRIPT_STYLE_RE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;
const HTML_TAG_RE = /<[^>]*>/g;
const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#039;"
};

export interface ChatbotProtocolOptions {
  timeoutMs?: number;
}

/**
 * Error shape used when REST message requests fail in ways the UI should make
 * visible to the visitor.
 */
export class ChatbotProtocolError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly details?: unknown;

  constructor(message: string, code: string, status?: number, details?: unknown) {
    super(message);
    this.name = "ChatbotProtocolError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export const createId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Ignore and use fallback below.
  }
  return `prestech-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

export const stripHtml = (text: unknown): string =>
  String(text ?? "").replace(SCRIPT_STYLE_RE, "").replace(HTML_TAG_RE, "").trim();

export const escapeHtml = (text: unknown): string =>
  String(text ?? "").replace(/[&<>"']/gu, (char) => HTML_ESCAPE_MAP[char] || char);

const renderInlineFormatting = (text: string): string =>
  text
    .replace(/\*([^*\n]+)\*/gu, "<strong>$1</strong>")
    .replace(/_([^_\n]+)_/gu, "<em>$1</em>")
    .replace(/\r?\n/gu, "<br>");

export const renderMessageHtml = (text: unknown): string =>
  escapeHtml(text)
    .split(/```([\s\S]*?)```/u)
    .map((segment, index) =>
      index % 2 === 1 ? `<code>${segment.replace(/\r?\n/gu, "<br>")}</code>` : renderInlineFormatting(segment)
    )
    .join("");

export const getSessionId = (storageKey: string): string => {
  if (typeof window === "undefined") {
    return createId();
  }

  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing && /^[a-zA-Z0-9_.:-]{8,128}$/u.test(existing)) {
      return existing;
    }
    const next = createId();
    window.localStorage.setItem(storageKey, next);
    return next;
  } catch {
    return createId();
  }
};

const normalizeEntry = (value: unknown): TranscriptEntry | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const source = value as Record<string, unknown>;
  const text = stripHtml(source.text ?? source.content ?? "");
  if (!text) {
    return null;
  }

  const direction = source.direction === "outgoing" ? "outgoing" : "incoming";
  const entry: TranscriptEntry = {
    id: String(source.id || createId()),
    direction,
    text,
    at: String(source.at || source.created_at || new Date().toISOString()),
    pending: Boolean(source.pending)
  };

  if (source.meta && typeof source.meta === "object") {
    const meta = source.meta as Record<string, unknown>;
    entry.meta = {
      handoffEmail: typeof meta.handoffEmail === "string" ? meta.handoffEmail : undefined,
      handoffEmailEnabled: typeof meta.handoffEmailEnabled === "boolean" ? meta.handoffEmailEnabled : undefined,
      conversationText: typeof meta.conversationText === "string" ? meta.conversationText : undefined
    };
  }

  return entry;
};

/**
 * Browser REST client for the Prestech WordPress chatbot endpoints.
 */
export class ChatbotProtocol {
  constructor(
    private readonly restUrl: string,
    private readonly nonce: string,
    private readonly sessionId: string,
    private readonly options: ChatbotProtocolOptions = {}
  ) {}

  async sendMessage(text: string): Promise<ChatMessageResponse | null> {
    const safeText = stripHtml(text).slice(0, 1200);
    if (!safeText) {
      return null;
    }

    const response = await this.fetchMessage({
      method: "POST",
      credentials: "same-origin",
      headers: this.headers(),
      body: JSON.stringify({
        sessionId: this.sessionId,
        message: safeText
      })
    });

    if (!response.ok) {
      throw await this.errorFromResponse(response);
    }

    const data = (await response.json()) as ChatMessageResponse;
    const messages = Array.isArray(data.messages)
      ? data.messages
          .map((entry) => normalizeEntry(entry))
          .filter((entry): entry is TranscriptEntry => entry !== null)
      : [];

    return { ...data, messages };
  }

  async fetchHistory(): Promise<ChatHistoryResponse | null> {
    const params = new URLSearchParams({ sessionId: this.sessionId, limit: "80" });
    const response = await fetch(`${this.restUrl}/history?${params.toString()}`, {
      credentials: "same-origin",
      headers: this.headers(false)
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as ChatHistoryResponse;
    const entries = Array.isArray(data.entries)
      ? data.entries
          .map((entry) => normalizeEntry(entry))
          .filter((entry): entry is TranscriptEntry => entry !== null)
      : [];

    return { ...data, entries };
  }

  async clearHistory(): Promise<boolean> {
    const params = new URLSearchParams({ sessionId: this.sessionId });
    const response = await fetch(`${this.restUrl}/history?${params.toString()}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: this.headers(false)
    });
    return response.ok;
  }

  private headers(withContentType = true): Record<string, string> {
    return {
      ...(withContentType ? { "Content-Type": "application/json" } : {}),
      "X-WP-Nonce": this.nonce
    };
  }

  /**
   * Sends message mutations with an AbortController so the UI cannot stay in
   * the pending state forever when the WordPress REST request hangs.
   */
  private async fetchMessage(init: RequestInit): Promise<Response> {
    if (typeof AbortController === "undefined") {
      return fetch(`${this.restUrl}/message`, init);
    }

    const controller = new AbortController();
    const timeout = globalThis.setTimeout(
      () => controller.abort(),
      Math.max(1, this.options.timeoutMs ?? DEFAULT_MESSAGE_TIMEOUT_MS)
    );

    try {
      return await fetch(`${this.restUrl}/message`, {
        ...init,
        signal: controller.signal
      });
    } catch (error) {
      if (this.isAbortError(error)) {
        throw new ChatbotProtocolError(
          "A resposta demorou mais do que o esperado.",
          "timeout"
        );
      }

      throw error;
    } finally {
      globalThis.clearTimeout(timeout);
    }
  }

  private async errorFromResponse(response: Response): Promise<ChatbotProtocolError> {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = null;
    }

    const message =
      details && typeof details === "object" && "message" in details
        ? String((details as { message?: unknown }).message)
        : `REST request failed with HTTP ${response.status}.`;

    return new ChatbotProtocolError(message, "http_error", response.status, details);
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof DOMException
      ? error.name === "AbortError"
      : error instanceof Error && error.name === "AbortError";
  }
}
